import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Wallet, Download, LayoutList, LayoutGrid, LineChart,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Line, Area, ReferenceLine, AreaChart, Cell, LabelList,
} from "recharts";
import { PageShell } from "@/components/SidebarLayout";
import MonthDrillPanel from "@/components/MonthDrillPanel";
import LineItemDrillPanel from "@/components/LineItemDrillPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useTheme } from "@/lib/ThemeContext";
import { api, formatApiError } from "@/lib/api";
import { formatCr } from "@/lib/format";

const CORAL = "#F43F5E";
const INDIGO = "#6366F1";
const EMERALD = "#10B981";
const AMBER = "#F59E0B";
const MUTED = "#94A3B8";

const VIEW_KEY = "cashflow_ledger_view";

/** Cashflow landing page — Excel-shaped matrix (rows = line items, columns = months). */
export default function CashflowOverview() {
  const { theme } = useTheme();
  const [horizon] = useState(17); // fetch window (workbook period); user-filter narrows within
  const [data, setData] = useState(null);
  const [drillMonth, setDrillMonth] = useState(null);
  const [drillKey, setDrillKey] = useState(null); // line-item drill
  const [selectedMonth, setSelectedMonth] = useState("range"); // waterfall — "range" = aggregate
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "detailed");
  const [tab, setTab] = useState("ledger");
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  const setViewPersisted = (v) => {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cashflow/ledger", { params: { horizon } });
      setData(data);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  }, [horizon]);

  useEffect(() => { load(); }, [load]);

  // Initialise the range picker to full period once data arrives.
  useEffect(() => {
    if (data?.months?.length) {
      if (!rangeStart) setRangeStart(data.months[0]);
      if (!rangeEnd)   setRangeEnd(data.months[data.months.length - 1]);
    }
  }, [data, rangeStart, rangeEnd]);

  // Client-side filter: slice the ledger to whatever range the user picked.
  const filtered = useMemo(() => {
    if (!data || !rangeStart || !rangeEnd) return null;
    return filterLedgerByRange(data, rangeStart, rangeEnd);
  }, [data, rangeStart, rangeEnd]);

  const onRangeChange = useCallback((s, e) => {
    setRangeStart(s);
    setRangeEnd(e);
    setSelectedMonth("range"); // reset waterfall to aggregate when range changes
  }, []);

  // A click anywhere on the page: sync the waterfall AND open the drill panel.
  const onCellClick = useCallback((m) => {
    if (!m) return;
    setSelectedMonth(m);
    setDrillMonth(m);
  }, []);

  const rangeSpan = filtered ? filtered.months.length : horizon;
  const rangeLabel = filtered ? `${filtered.month_labels[0]} → ${filtered.month_labels[filtered.month_labels.length - 1]}` : `${horizon}m`;

  return (
    <PageShell
      testId="cashflow-overview-page"
      kicker="Company-Wide Cashflow"
      title="Cashflow"
      hideProjectFilter
      rangeLabel={rangeLabel}
      rangeMonths={filtered?.months}
      actions={
        data && rangeStart && rangeEnd ? (
          <MonthRangePicker
            months={data.months}
            labels={data.month_labels}
            start={rangeStart}
            end={rangeEnd}
            onChange={onRangeChange}
          />
        ) : null
      }
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-3xl">
        Excel-parity ledger. Rows = line items, columns = months.
        <b> Surplus / Deficit = Opening + Inflow − (Outflow + Debt + Committed Lands)</b> and rolls forward as next month&apos;s Opening.
      </p>

      {filtered && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Hero icon={Wallet} label="Opening (A)" value={filtered.opening_balance} accent={MUTED} testId="cf-hero-opening" />
          <Hero icon={TrendingUp} label={`Inflows · ${rangeSpan}m`} value={filtered.totals.inflow} accent={EMERALD} testId="cf-hero-inflows" />
          <Hero icon={TrendingDown} label={`Total Outflow · ${rangeSpan}m`} value={filtered.totals.outflow_all} accent={CORAL} negative testId="cf-hero-outflows" />
          <Hero icon={Wallet} label={`Closing · ${rangeSpan}m`} value={filtered.closing_balance} accent={INDIGO} testId="cf-hero-closing" bold />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} data-testid="cf-tabs">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList data-testid="cf-tabs-list">
            <TabsTrigger value="ledger" data-testid="cf-tab-ledger">
              <LayoutList size={14} className="mr-1.5" /> Ledger
            </TabsTrigger>
            <TabsTrigger value="chart" data-testid="cf-tab-chart">
              <LineChart size={14} className="mr-1.5" /> Chart
            </TabsTrigger>
          </TabsList>

          {tab === "ledger" && filtered && (
            <div className="flex items-center gap-2">
              <ViewToggle view={view} setView={setViewPersisted} />
              <DownloadButton horizon={horizon} />
            </div>
          )}
        </div>

        <TabsContent value="ledger">
          {filtered && (
            <LedgerMatrix
              data={filtered}
              view={view}
              onMonthClick={onCellClick}
              onRowClick={setDrillKey}
            />
          )}
        </TabsContent>

        <TabsContent value="chart">
          {filtered && (
            <LedgerChart
              data={filtered}
              theme={theme}
              onMonthClick={onCellClick}
              selectedMonth={selectedMonth}
              onSelectMonth={setSelectedMonth}
            />
          )}
        </TabsContent>
      </Tabs>

      {drillMonth && (
        <MonthDrillPanel month={drillMonth} horizon={horizon} onClose={() => setDrillMonth(null)} />
      )}

      {drillKey && (
        <LineItemDrillPanel rowKey={drillKey} horizon={horizon} onClose={() => setDrillKey(null)} />
      )}
    </PageShell>
  );
}

/* ─────────────────────────── Ledger Matrix ─────────────────────────── */

function LedgerMatrix({ data, view, onMonthClick, onRowClick }) {
  const rows = useMemo(() => {
    if (view === "consolidated") {
      return data.rows.filter((r) => r.depth === 0);
    }
    return data.rows;
  }, [data, view]);

  const { months, month_labels: monthLabels } = data;

  return (
    <div className="cf-card p-0" data-testid="cf-ledger">
      <div className="overflow-x-auto overflow-y-auto max-h-[70vh] rounded-2xl">
        <table className="min-w-full text-[11px] md:text-xs border-collapse" data-testid="cf-ledger-table">
          <thead className="sticky top-0 z-30">
            <tr>
              <th
                className="text-left px-2 md:px-3 py-2.5 font-semibold uppercase tracking-widest text-[10px] text-muted-dark sticky left-0 z-40 border-r border-hairline min-w-[150px] md:min-w-[260px]"
                style={{ background: "var(--cf-surface-2)" }}
              >
                Line Item
              </th>
              {months.map((m, i) => (
                <th
                  key={m}
                  className="text-right px-3 py-2.5 font-semibold uppercase tracking-widest text-[10px] text-muted-dark whitespace-nowrap cursor-pointer hover:text-ink transition-colors min-w-[80px] md:min-w-[86px]"
                  style={{ background: "var(--cf-surface-2)" }}
                  onClick={() => onMonthClick(m)}
                  data-testid={`cf-ledger-col-${m}`}
                  title={`Open ${monthLabels[i]} drill-down`}
                >
                  {monthLabels[i]}
                </th>
              ))}
              <th
                className="text-right px-2 md:px-3 py-2.5 font-semibold uppercase tracking-widest text-[10px] text-muted-dark sticky right-0 z-40 border-l border-hairline min-w-[84px] md:min-w-[100px]"
                style={{ background: "var(--cf-surface-2)" }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <LedgerRow
                key={r.key}
                row={r}
                months={months}
                onMonthClick={onMonthClick}
                onRowClick={onRowClick}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Row style resolver — background + font weight by type. */
function rowStyle(row) {
  const type = row.type;
  const accent = row.accent;
  const base = { background: "var(--cf-surface)" };
  if (type === "section_header") {
    // Solid section band using accent w/ opacity mixed against surface.
    return {
      background: mixAccent(accent, 0.14),
      color: "var(--cf-ink, inherit)",
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      fontSize: "10.5px",
      borderTop: "1px solid var(--cf-hairline-strong)",
    };
  }
  if (type === "group_header") {
    return {
      background: mixAccent(accent, 0.06),
      fontWeight: 700,
      fontSize: "11px",
    };
  }
  if (type === "subtotal") {
    return {
      background: mixAccent(accent, 0.10),
      fontWeight: 800,
      borderTop: "1px solid var(--cf-hairline)",
    };
  }
  if (type === "totals_row") {
    return {
      background: mixAccent(accent, 0.12),
      fontWeight: 800,
      borderTop: "1px solid var(--cf-hairline-strong)",
    };
  }
  if (type === "grand") {
    return {
      background: mixAccent(accent, 0.20),
      fontWeight: 900,
      fontSize: "12px",
      borderTop: "2px solid var(--cf-hairline-strong)",
      borderBottom: "2px solid var(--cf-hairline-strong)",
    };
  }
  if (type === "opening_row") {
    return {
      background: mixAccent(accent, 0.10),
      fontWeight: 700,
      borderBottom: "1px solid var(--cf-hairline)",
    };
  }
  return base;
}

/** Convert #RRGGBB accent + alpha → rgba(). */
function mixAccent(hex, alpha) {
  const h = (hex || "#94A3B8").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function LedgerRow({ row, months, onMonthClick, onRowClick }) {
  const style = rowStyle(row);
  const tint = style.background || null;
  const rowDrillable = (
    row.type === "detail" || row.type === "group_header"
  ) && !["opening", "net"].includes(row.section);
  // Sticky cells must be OPAQUE to occlude horizontally-scrolled cells behind
  // them. Layer the accent tint (rgba) as a background-image on top of an
  // opaque surface base — this preserves the section banding hue while
  // guaranteeing full occlusion when the user scrolls.
  const stickyStyle = tint
    ? {
        backgroundColor: "var(--cf-surface)",
        backgroundImage: `linear-gradient(${tint}, ${tint})`,
      }
    : { backgroundColor: "var(--cf-surface)" };
  const negativeSection = row.section === "outflow" || row.section === "debt" || row.section === "committed_lands";
  const indent = row.depth === 2 ? "pl-8" : row.depth === 1 ? "pl-4" : "pl-3";

  return (
    <tr
      className="border-b border-hairline/40 hover:brightness-[0.98] transition-all"
      data-testid={`cf-row-${row.key}`}
      style={style}
    >
      <td
        className={`${indent} pr-2 md:pr-3 py-1.5 sticky left-0 z-10 border-r border-hairline/60 text-ink whitespace-nowrap min-w-[150px] md:min-w-[260px] max-w-[150px] md:max-w-none truncate md:overflow-visible ${rowDrillable ? "cursor-pointer hover:text-accent" : ""}`}
        style={stickyStyle}
        title={rowDrillable ? `${row.label} — click for detailed breakdown` : row.label}
        onClick={rowDrillable && onRowClick ? () => onRowClick(row.key) : undefined}
        data-testid={`cf-row-label-${row.key}`}
      >
        {row.label}
        {rowDrillable && <span className="ml-1.5 text-[9px] text-muted-light">▸</span>}
      </td>
      {months.map((m) => {
        const v = row.values?.[m] || 0;
        const isDetail = row.type === "detail";
        return (
          <td
            key={m}
            className={`px-3 py-1.5 text-right num tabular-nums whitespace-nowrap min-w-[80px] md:min-w-[86px] ${isDetail ? "cursor-pointer" : ""}`}
            onClick={isDetail ? () => onMonthClick(m) : undefined}
            data-testid={`cf-cell-${row.key}-${m}`}
          >
            <MoneyCell v={v} negative={negativeSection && row.type === "detail"} row={row} />
          </td>
        );
      })}
      <td
        className="px-2 md:px-3 py-1.5 text-right num tabular-nums whitespace-nowrap sticky right-0 z-10 border-l border-hairline/60 min-w-[84px] md:min-w-[100px]"
        style={stickyStyle}
      >
        <MoneyCell v={row.total} negative={negativeSection && row.type === "detail"} row={row} bold />
      </td>
    </tr>
  );
}

function MoneyCell({ v, negative, row, bold }) {
  if (!v && v !== 0) return <span className="text-muted-light">–</span>;
  if (Math.abs(v) < 0.005) return <span className="text-muted-light">–</span>;

  const isNegSection = row.section === "outflow" || row.section === "debt" || row.section === "committed_lands";
  // A NEGATIVE value inside an outflow section is money coming back in
  // (e.g. Land Payment - Binny repayment) — render it green and unsigned.
  const inflowBack = isNegSection && row.type === "detail" && v < 0;

  let color;
  if (inflowBack) color = EMERALD;
  else if (row.section === "opening" || row.section === "net") color = "var(--cf-ink, inherit)";
  else if (row.section === "inflow") color = EMERALD;
  else if (isNegSection) color = CORAL;

  const displayNeg = negative && !inflowBack && row.section !== "opening" && row.section !== "net";
  const surplusRow = row.key === "surplus";
  const isNegSurplus = surplusRow && v < 0;
  return (
    <span
      className={bold ? "font-bold" : ""}
      style={{ color: isNegSurplus ? "#DC2626" : color }}
    >
      {displayNeg ? "−" : !inflowBack && v < 0 ? "−" : ""}
      {formatCr(Math.abs(v), { showZero: false })}
    </span>
  );
}

/* ─────────────────────────── Toolbar bits ─────────────────────────── */

function ViewToggle({ view, setView }) {
  const btn = (val, Icon, label) => {
    const active = view === val;
    return (
      <button
        key={val}
        type="button"
        onClick={() => setView(val)}
        data-testid={`cf-view-${val}`}
        className={`cf-touch px-3 py-1.5 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${
          active ? "bg-ink text-paper" : "text-muted-dark hover:text-ink"
        }`}
      >
        <Icon size={13} /> {label}
      </button>
    );
  };
  return (
    <div
      className="inline-flex items-center gap-1 p-1 bg-surface rounded-full"
      style={{ boxShadow: "0 1px 2px rgba(18,49,89,0.05), 0 6px 16px rgba(18,49,89,0.06)" }}
      data-testid="cf-view-toggle"
    >
      {btn("detailed", LayoutList, "Detailed")}
      {btn("consolidated", LayoutGrid, "Consolidated")}
    </div>
  );
}

function DownloadButton({ horizon }) {
  const onClick = async () => {
    try {
      const res = await api.get("/cashflow/ledger/export.xlsx", {
        params: { horizon },
        responseType: "blob",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `cashflow_ledger_${horizon}m.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Could not download the Excel export.");
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="cf-download-xlsx"
      className="cf-touch px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-ink text-paper hover:opacity-90 transition-opacity"
    >
      <Download size={13} /> Download Excel
    </button>
  );
}

/* ─────────────────────────── Chart tab ─────────────────────────── */

// Palette for outflow-composition stack (aligned with SECTION_META in backend).
const PAGE_COLORS = {
  development:     "#F43F5E",  // coral (primary)
  epc:             "#FB7185",  // rose
  marketing:       "#F59E0B",  // amber
  hr:              "#8B5CF6",  // violet
  statutory:       "#0EA5E9",  // sky
  debt:            "#EA580C",  // orange
  committed_lands: "#7C3AED",  // deep violet
};
const INFLOW_COLORS = {
  collections: "#10B981",  // emerald
  drawdowns:   "#F59E0B",  // amber
  other:       "#0EA5E9",  // sky
};

/** Build every chart series once from the ledger row stream. Charts read
 *  from `series[]` so no per-chart lookups are needed. */
function buildSeries(data) {
  const rowByKey = Object.fromEntries(data.rows.map((r) => [r.key, r]));
  const get = (key, m) => Number(rowByKey[key]?.values?.[m] || 0);

  let cumSurplus = 0;
  return data.months.map((m, i) => {
    const inflow  = get("inflow_subtotal", m);
    const opOut   = get("outflow_subtotal", m);
    const debt    = get("debt_subtotal", m);
    const cl      = get("cl_subtotal", m);
    const totOut  = get("total_outflow", m);
    const surplus = get("surplus", m);
    const closing = get("closing", m);
    cumSurplus += surplus;
    const coverage = totOut > 0 ? inflow / totOut : (inflow > 0 ? 2 : 1);
    return {
      month: m,
      label: data.month_labels[i],
      // A. main
      inflow, outflow: totOut, surplus, closing,
      // 1. running closing = closing (already computed)
      // 3. outflow composition (₹L per page + debt + cl)
      development: get("outflow_group::development", m),
      epc:         get("outflow_group::epc", m),
      marketing:   get("outflow_group::marketing", m),
      hr:          get("outflow_group::hr", m),
      statutory:   get("outflow_group::statutory", m),
      debt,
      committed_lands: cl,
      // 4. inflow mix
      collections: get("inflow_collections", m),
      drawdowns:   get("inflow_drawdowns", m),
      other:       get("inflow_other", m),
      // 5. debt split
      interest:  get("debt_interest", m),
      principal: get("debt_principal", m),
      op_outflow: opOut,
      // 6. coverage
      coverage: Number(coverage.toFixed(3)),
      // extras
      cum_surplus: cumSurplus,
      opening: get("opening", m),
    };
  });
}

const money = (v) => formatCr(v);
const crAxis = (v) => `${Math.round(v / 100)}`;

function ChartCard({ title, subtitle, legend, children, wide, testId }) {
  return (
    <section
      className={`cf-card p-4 md:p-5 ${wide ? "md:col-span-2" : ""}`}
      data-testid={testId}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-display text-sm md:text-base text-ink font-bold truncate">{title}</h3>
          {subtitle && <p className="text-[11px] text-muted-dark">{subtitle}</p>}
        </div>
        {legend && (
          <div className="flex items-center gap-2 md:gap-3 text-[10.5px] text-muted-dark flex-wrap">
            {legend}
          </div>
        )}
      </div>
      <div className="h-64 md:h-72 -mx-2 md:mx-0">
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
      <p className="text-[10px] text-muted-light mt-1">Y-axis in ₹ Cr</p>
    </section>
  );
}

const tooltipStyle = {
  background: "var(--cf-surface-glass-strong)",
  border: "1px solid var(--cf-hairline)",
  borderRadius: 8,
  fontSize: 11,
};

function LedgerChart({ data, theme, onMonthClick, selectedMonth, onSelectMonth }) {
  const grid = theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const axis = theme === "dark" ? "#94A3B8" : "#64748B";
  const series = useMemo(() => buildSeries(data), [data]);

  const commonAxes = (
    <>
      <CartesianGrid stroke={grid} vertical={false} />
      <XAxis dataKey="label" tick={{ fill: axis, fontSize: 10 }} interval="preserveStartEnd" />
      <YAxis tick={{ fill: axis, fontSize: 10 }} tickFormatter={crAxis} width={36} />
    </>
  );

  const onBarClick = (e) => e && e.month && onMonthClick(e.month);

  return (
    <div className="space-y-4">
      {/* A — Main chart: Inflow + Outflow bars, Surplus line, Closing dashed line */}
      <ChartCard
        wide
        testId="cf-chart-main"
        title="Monthly cashflow"
        subtitle="Bars: Inflow vs Total Outflow. Line: Surplus / (Deficit). Dashed: Closing Balance. Click any bar for the month drill-down."
        legend={
          <>
            <Legend color={EMERALD} label="Inflow" />
            <Legend color={CORAL}   label="Outflow" />
            <Legend color={INDIGO}  label="Surplus / (Deficit)" />
            <Legend color="#64748B" label="Closing Balance" dashed />
          </>
        }
      >
        <ComposedChart data={series} margin={{ top: 10, right: 10, bottom: 8, left: -8 }}>
          {commonAxes}
          <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [money(v), name]} />
          <ReferenceLine y={0} stroke={axis} strokeDasharray="3 3" />
          <Bar  dataKey="inflow"  name="Inflow"  fill={EMERALD} radius={[3, 3, 0, 0]} cursor="pointer" onClick={onBarClick} />
          <Bar  dataKey="outflow" name="Outflow" fill={CORAL}   radius={[3, 3, 0, 0]} cursor="pointer" onClick={onBarClick} />
          <Line dataKey="surplus" name="Surplus / (Deficit)" stroke={INDIGO} strokeWidth={2.5} dot={{ r: 3, fill: INDIGO }} activeDot={{ r: 5 }} />
          <Line dataKey="closing" name="Closing Balance"     stroke="#64748B" strokeWidth={1.8} strokeDasharray="4 4" dot={false} />
        </ComposedChart>
      </ChartCard>

      {/* Waterfall — Opening → Closing for a selected month */}
      <WaterfallCard
        data={data}
        series={series}
        selectedMonth={selectedMonth}
        onSelectMonth={onSelectMonth}
        theme={theme}
        grid={grid}
        axis={axis}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cf-chart-grid">
        {/* 1 — Running Closing Balance (area) */}
        <ChartCard
          testId="cf-chart-closing"
          title="Treasury Trajectory"
          subtitle="Running closing balance. Dips below zero flag a cash crunch."
        >
          <AreaChart data={series} margin={{ top: 10, right: 10, bottom: 8, left: -8 }}>
            <defs>
              <linearGradient id="closingFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={INDIGO} stopOpacity={0.35} />
                <stop offset="100%" stopColor={INDIGO} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            {commonAxes}
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [money(v), "Closing"]} />
            <ReferenceLine y={0} stroke={CORAL} strokeDasharray="3 3" label={{ value: "Zero cash", position: "insideLeft", fill: CORAL, fontSize: 9 }} />
            <Area type="monotone" dataKey="closing" name="Closing" stroke={INDIGO} strokeWidth={2} fill="url(#closingFill)" />
          </AreaChart>
        </ChartCard>

        {/* 3 — Outflow Composition (stacked area) */}
        <ChartCard
          testId="cf-chart-outflow-mix"
          title="Outflow Composition"
          subtitle="What's eating cash each month. Stacked area — the top edge is total monthly outflow."
          legend={
            <>
              <Legend color={PAGE_COLORS.development} label="Development" />
              <Legend color={PAGE_COLORS.epc}         label="EPC" />
              <Legend color={PAGE_COLORS.marketing}   label="Marketing" />
              <Legend color={PAGE_COLORS.hr}          label="HR" />
              <Legend color={PAGE_COLORS.statutory}   label="Statutory" />
              <Legend color={PAGE_COLORS.debt}        label="Debt" />
              <Legend color={PAGE_COLORS.committed_lands} label="Committed Lands" />
            </>
          }
        >
          <AreaChart data={series} margin={{ top: 10, right: 10, bottom: 8, left: -8 }}>
            {commonAxes}
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money(v), n]} />
            <Area type="monotone" dataKey="development"     name="Development"     stackId="o" stroke={PAGE_COLORS.development}     fill={PAGE_COLORS.development}     fillOpacity={0.75} />
            <Area type="monotone" dataKey="epc"             name="EPC"             stackId="o" stroke={PAGE_COLORS.epc}             fill={PAGE_COLORS.epc}             fillOpacity={0.75} />
            <Area type="monotone" dataKey="marketing"       name="Marketing"       stackId="o" stroke={PAGE_COLORS.marketing}       fill={PAGE_COLORS.marketing}       fillOpacity={0.75} />
            <Area type="monotone" dataKey="hr"              name="HR"              stackId="o" stroke={PAGE_COLORS.hr}              fill={PAGE_COLORS.hr}              fillOpacity={0.75} />
            <Area type="monotone" dataKey="statutory"       name="Statutory"       stackId="o" stroke={PAGE_COLORS.statutory}       fill={PAGE_COLORS.statutory}       fillOpacity={0.75} />
            <Area type="monotone" dataKey="debt"            name="Debt Service"    stackId="o" stroke={PAGE_COLORS.debt}            fill={PAGE_COLORS.debt}            fillOpacity={0.75} />
            <Area type="monotone" dataKey="committed_lands" name="Committed Lands" stackId="o" stroke={PAGE_COLORS.committed_lands} fill={PAGE_COLORS.committed_lands} fillOpacity={0.8} />
          </AreaChart>
        </ChartCard>

        {/* 4 — Inflow Mix (stacked area) */}
        <ChartCard
          testId="cf-chart-inflow-mix"
          title="Inflow Mix"
          subtitle="Where each rupee comes from. Stacked area — the top edge is total monthly inflow."
          legend={
            <>
              <Legend color={INFLOW_COLORS.collections} label="Sales Collections" />
              <Legend color={INFLOW_COLORS.drawdowns}   label="Loan Drawdowns" />
              <Legend color={INFLOW_COLORS.other}       label="Other Receipts" />
            </>
          }
        >
          <AreaChart data={series} margin={{ top: 10, right: 10, bottom: 8, left: -8 }}>
            {commonAxes}
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money(v), n]} />
            <Area type="monotone" dataKey="collections" name="Sales Collections" stackId="i" stroke={INFLOW_COLORS.collections} fill={INFLOW_COLORS.collections} fillOpacity={0.75} />
            <Area type="monotone" dataKey="drawdowns"   name="Loan Drawdowns"    stackId="i" stroke={INFLOW_COLORS.drawdowns}   fill={INFLOW_COLORS.drawdowns}   fillOpacity={0.75} />
            <Area type="monotone" dataKey="other"       name="Other Receipts"    stackId="i" stroke={INFLOW_COLORS.other}       fill={INFLOW_COLORS.other}       fillOpacity={0.8} />
          </AreaChart>
        </ChartCard>

        {/* 5 — Debt Service Split (grouped bar) */}
        <ChartCard
          testId="cf-chart-debt-split"
          title="Debt Service — Interest vs Principal"
          subtitle="How much of monthly debt is interest carry vs principal repayment."
          legend={
            <>
              <Legend color={AMBER}   label="Interest" />
              <Legend color="#DB2777" label="Principal" />
            </>
          }
        >
          <ComposedChart data={series} margin={{ top: 10, right: 10, bottom: 8, left: -8 }}>
            {commonAxes}
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money(v), n]} />
            <Bar dataKey="interest"  name="Interest"  fill={AMBER}     radius={[3, 3, 0, 0]} cursor="pointer" onClick={onBarClick} />
            <Bar dataKey="principal" name="Principal" fill="#DB2777"   radius={[3, 3, 0, 0]} cursor="pointer" onClick={onBarClick} />
          </ComposedChart>
        </ChartCard>

        {/* 6 — Coverage Ratio (line, 1.0 threshold) */}
        <ChartCard
          testId="cf-chart-coverage"
          title="Coverage Ratio"
          subtitle="Inflow ÷ Total Outflow. Below 1.0 means we are burning reserves that month."
          legend={
            <>
              <Legend color={EMERALD} label="Self-funding (≥ 1.0)" />
              <Legend color={CORAL}   label="Burning reserves" />
            </>
          }
        >
          <ComposedChart data={series} margin={{ top: 10, right: 10, bottom: 8, left: -8 }}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="label" tick={{ fill: axis, fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: axis, fontSize: 10 }} tickFormatter={(v) => `${v.toFixed(1)}×`} width={40} domain={[0, "dataMax + 0.5"]} />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v) => [`${Number(v).toFixed(2)}×`, "Coverage"]}
            />
            <ReferenceLine y={1} stroke={CORAL} strokeDasharray="4 4" label={{ value: "1.0×", position: "right", fill: CORAL, fontSize: 10 }} />
            <Bar dataKey="coverage" name="Coverage" radius={[3, 3, 0, 0]} cursor="pointer" onClick={onBarClick}>
              {series.map((s, i) => (
                <CoverageCell key={i} v={s.coverage} />
              ))}
            </Bar>
          </ComposedChart>
        </ChartCard>
      </div>
    </div>
  );
}

/** Individual bar recolour based on coverage < 1. Recharts uses <Cell> for
 *  this pattern. */
function CoverageCell({ v }) {
  return <Cell fill={v >= 1 ? EMERALD : CORAL} />;
}

/* ─────────────────────────── Waterfall chart ─────────────────────────── */

const WATERFALL_STEPS = [
  { key: "opening",         label: "Opening",       type: "start", color: "#64748B" },
  { key: "collections",     label: "Sales",         type: "in",    color: INFLOW_COLORS.collections },
  { key: "drawdowns",       label: "Drawdowns",     type: "in",    color: INFLOW_COLORS.drawdowns },
  { key: "other",           label: "Other Rec.",    type: "in",    color: INFLOW_COLORS.other },
  { key: "development",     label: "Development",   type: "out",   color: PAGE_COLORS.development },
  { key: "epc",             label: "EPC",           type: "out",   color: PAGE_COLORS.epc },
  { key: "marketing",       label: "Marketing",     type: "out",   color: PAGE_COLORS.marketing },
  { key: "hr",              label: "HR",            type: "out",   color: PAGE_COLORS.hr },
  { key: "statutory",       label: "Statutory",     type: "out",   color: PAGE_COLORS.statutory },
  { key: "debt",            label: "Debt Service",  type: "out",   color: PAGE_COLORS.debt },
  { key: "committed_lands", label: "Comm. Lands",   type: "out",   color: PAGE_COLORS.committed_lands },
  { key: "closing",         label: "Closing",       type: "end",   color: INDIGO },
];

/** Build waterfall bar data: each row = { label, spacer, delta, color, running, raw }.
 *  `spacer` is invisible base; `delta` is the visible bar height. Zero-value
 *  in/out steps are dropped to reduce clutter. */
function buildWaterfall(monthSeries) {
  if (!monthSeries) return [];
  let running = 0;
  const bars = [];
  for (const step of WATERFALL_STEPS) {
    const raw = Number(monthSeries[step.key] || 0);
    if (step.type === "start") {
      running = raw;
      bars.push({ label: step.label, spacer: 0, delta: raw, color: step.color, running, raw, type: step.type });
    } else if (step.type === "in") {
      if (Math.abs(raw) < 0.005) continue;
      bars.push({ label: step.label, spacer: running, delta: raw, color: step.color, running: running + raw, raw, type: step.type });
      running += raw;
    } else if (step.type === "out") {
      if (Math.abs(raw) < 0.005) continue;
      const after = running - raw;
      bars.push({ label: step.label, spacer: after, delta: raw, color: step.color, running: after, raw: -raw, type: step.type });
      running = after;
    } else if (step.type === "end") {
      // Prefer the ledger's closing value if available (defensive vs float drift).
      const closing = Number(monthSeries.closing ?? running);
      bars.push({ label: step.label, spacer: 0, delta: closing, color: step.color, running: closing, raw: closing, type: step.type });
    }
  }
  return bars;
}

function WaterfallTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const sign = d.type === "out" ? "−" : d.type === "in" ? "+" : "";
  return (
    <div className="rounded-md p-2 text-[11px] shadow-lg" style={tooltipStyle}>
      <div className="font-semibold text-ink mb-1">{d.label}</div>
      {d.type !== "start" && d.type !== "end" && (
        <div className="text-muted-dark">Change: <span className="font-semibold" style={{ color: d.color }}>{sign}{formatCr(Math.abs(d.raw))}</span></div>
      )}
      <div className="text-muted-dark">Running: <span className="font-semibold text-ink">{formatCr(d.running)}</span></div>
    </div>
  );
}

function WaterfallCard({ data, series, selectedMonth, onSelectMonth, grid, axis }) {
  const isRange = !selectedMonth || selectedMonth === "range" || !data.months.includes(selectedMonth);
  const monthSeries = isRange ? aggregateSeries(series, data) : series[data.months.indexOf(selectedMonth)];
  const bars = useMemo(() => buildWaterfall(monthSeries), [monthSeries]);

  const openingVal = monthSeries?.opening || 0;
  const closingVal = monthSeries?.closing || 0;
  const surplusVal = monthSeries?.surplus || 0;

  const rangeLabelStr = data.month_labels.length
    ? `${data.month_labels[0]} → ${data.month_labels[data.month_labels.length - 1]}`
    : "Selected period";
  const subtitle = isRange
    ? `Aggregate breakdown across the selected period (${rangeLabelStr}). Every inflow adds, every outflow subtracts to land on Closing.`
    : "Single-month breakdown: how each inflow adds and each outflow subtracts to land on the closing balance.";

  // Bar minimum width for mobile horizontal scroll — keeps 12 labels legible.
  const minChartWidth = Math.max(560, bars.length * 56);

  return (
    <section className="cf-card p-4 md:p-5" data-testid="cf-chart-waterfall">
      <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm md:text-base text-ink font-bold">
            Waterfall — {isRange ? "Selected period" : (data.month_labels[data.months.indexOf(selectedMonth)] || "")}
          </h3>
          <p className="text-[11px] text-muted-dark">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-light font-semibold hidden md:inline">View</span>
          <select
            data-testid="cf-waterfall-month"
            value={isRange ? "range" : selectedMonth}
            onChange={(e) => onSelectMonth(e.target.value)}
            className="cf-touch text-xs font-semibold rounded-full px-3 py-1.5 bg-ink text-paper cursor-pointer outline-none focus:ring-2 focus:ring-offset-1 focus:ring-ink/40"
          >
            <option value="range">Selected period</option>
            {data.months.map((m, i) => (
              <option key={m} value={m}>{data.month_labels[i]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <MiniStat label="Opening" value={openingVal} accent="#64748B" testId="cf-waterfall-opening" />
        <MiniStat label="Surplus / (Deficit)" value={surplusVal} accent={surplusVal < 0 ? CORAL : INDIGO} signed testId="cf-waterfall-surplus" />
        <MiniStat label="Closing" value={closingVal} accent={INDIGO} bold testId="cf-waterfall-closing" />
      </div>

      <div className="overflow-x-auto -mx-2 md:mx-0" data-testid="cf-waterfall-scroll">
        <div style={{ minWidth: minChartWidth }} className="h-72 md:h-80">
          <ResponsiveContainer>
            <ComposedChart data={bars} margin={{ top: 20, right: 10, bottom: 8, left: -8 }}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="label" tick={{ fill: axis, fontSize: 10 }} interval={0} angle={-22} textAnchor="end" height={58} />
              <YAxis tick={{ fill: axis, fontSize: 10 }} tickFormatter={crAxis} width={40} />
              <Tooltip content={<WaterfallTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
              <ReferenceLine y={0} stroke={axis} strokeDasharray="3 3" />
              <Bar dataKey="spacer" stackId="w" fill="transparent" isAnimationActive={false} />
              <Bar dataKey="delta"  stackId="w" radius={[3, 3, 0, 0]}>
                {bars.map((b, i) => <Cell key={i} fill={b.color} />)}
                <LabelList
                  dataKey="raw"
                  position="top"
                  formatter={(v) => {
                    const n = Number(v);
                    if (Math.abs(n) < 0.5) return "";
                    return formatCr(Math.abs(n));
                  }}
                  style={{ fill: "var(--cf-ink)", fontSize: 9, fontWeight: 600 }}
                />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <p className="text-[10px] text-muted-light mt-1">Y-axis in ₹ Cr · Bar labels show change; hover for running balance. Swipe on mobile to scroll all 12 steps.</p>
    </section>
  );
}

/** Aggregate a series across all filtered months for the "Selected period"
 *  waterfall view. Opening = first-month opening; Closing = last-month closing;
 *  every additive category = sum. */
function aggregateSeries(series, data) {
  if (!series?.length) return null;
  const first = series[0];
  const last = series[series.length - 1];
  const sum = (k) => series.reduce((s, x) => s + Number(x[k] || 0), 0);
  return {
    opening: Number(first?.opening ?? data.opening_balance ?? 0),
    closing: Number(last?.closing ?? data.closing_balance ?? 0),
    surplus: sum("surplus"),
    collections: sum("collections"),
    drawdowns:   sum("drawdowns"),
    other:       sum("other"),
    development: sum("development"),
    epc:         sum("epc"),
    marketing:   sum("marketing"),
    hr:          sum("hr"),
    statutory:   sum("statutory"),
    debt:        sum("debt"),
    committed_lands: sum("committed_lands"),
  };
}

/** Client-side slice of the ledger to a [start, end] month range. Recomputes
 *  each row's `total` and the top-level opening/closing/totals so KPIs, ledger
 *  table and charts all reflect just the selected window. */
function filterLedgerByRange(data, start, end) {
  const s = data.months.indexOf(start);
  const e = data.months.indexOf(end);
  if (s < 0 || e < 0 || e < s) return data;
  const months = data.months.slice(s, e + 1);
  const labels = data.month_labels.slice(s, e + 1);
  const last = months[months.length - 1];
  const first = months[0];

  const rows = data.rows.map((r) => {
    const values = Object.fromEntries(months.map((m) => [m, Number(r.values?.[m] || 0)]));
    let total;
    if (r.key === "opening")      total = values[first];
    else if (r.key === "closing") total = values[last];
    else total = months.reduce((acc, m) => acc + values[m], 0);
    return { ...r, values, total: Math.round(total * 100) / 100 };
  });

  const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));
  return {
    ...data,
    months,
    month_labels: labels,
    opening_balance: byKey.opening?.total ?? data.opening_balance,
    closing_balance: byKey.closing?.total ?? data.closing_balance,
    totals: {
      inflow: byKey.inflow_subtotal?.total ?? 0,
      outflow_operating: byKey.outflow_subtotal?.total ?? 0,
      debt_service:      byKey.debt_subtotal?.total ?? 0,
      committed_lands:   byKey.cl_subtotal?.total ?? 0,
      outflow_all:       byKey.total_outflow?.total ?? 0,
      surplus:           byKey.surplus?.total ?? 0,
    },
    rows,
  };
}

/** Custom range picker — quick presets (3m / 6m / 12m / Full) + From / To
 *  dropdowns filled from the raw workbook months. Chairman-friendly. */
function MonthRangePicker({ months, labels, start, end, onChange }) {
  const startIdx = months.indexOf(start);
  const endIdx   = months.indexOf(end);

  const applyPreset = (n) => {
    const total = months.length;
    const span = Math.min(n, total);
    onChange(months[0], months[span - 1]);
  };
  const isPreset = (n) => startIdx === 0 && endIdx === Math.min(n, months.length) - 1;

  const btn = (label, active, onClick, testId) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`cf-touch px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
        active ? "bg-ink text-paper" : "text-muted-dark hover:text-ink"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="cf-range-picker">
      <div
        className="inline-flex items-center gap-1 p-1 bg-surface rounded-full"
        style={{ boxShadow: "0 1px 2px rgba(18,49,89,0.05), 0 6px 16px rgba(18,49,89,0.06)" }}
      >
        {btn("3m",   isPreset(3),  () => applyPreset(3),  "cf-range-3m")}
        {btn("6m",   isPreset(6),  () => applyPreset(6),  "cf-range-6m")}
        {btn("12m",  isPreset(12), () => applyPreset(12), "cf-range-12m")}
        {btn(`${months.length}m`, isPreset(months.length), () => applyPreset(months.length), "cf-range-full")}
      </div>
      <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-dark bg-surface rounded-full px-2 py-1"
           style={{ boxShadow: "0 1px 2px rgba(18,49,89,0.05), 0 6px 16px rgba(18,49,89,0.06)" }}>
        <span className="uppercase tracking-widest text-[9px] font-semibold text-muted-light pl-1">From</span>
        <select
          data-testid="cf-range-from"
          value={start}
          onChange={(e) => {
            const s = e.target.value;
            const sIdx = months.indexOf(s);
            onChange(s, sIdx > endIdx ? s : end);
          }}
          className="cf-touch text-[11px] font-semibold rounded-full px-2 py-1 bg-transparent text-ink cursor-pointer outline-none"
        >
          {months.map((m, i) => (
            <option key={m} value={m} disabled={i > endIdx}>{labels[i]}</option>
          ))}
        </select>
        <span className="text-muted-light">→</span>
        <select
          data-testid="cf-range-to"
          value={end}
          onChange={(e) => {
            const t = e.target.value;
            const tIdx = months.indexOf(t);
            onChange(tIdx < startIdx ? t : start, t);
          }}
          className="cf-touch text-[11px] font-semibold rounded-full px-2 py-1 bg-transparent text-ink cursor-pointer outline-none"
        >
          {months.map((m, i) => (
            <option key={m} value={m} disabled={i < startIdx}>{labels[i]}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent, signed, bold, testId }) {
  const negative = value < 0;
  return (
    <div className="rounded-lg px-3 py-2" style={{ background: "var(--cf-surface-2)" }} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest text-muted-light font-semibold flex items-center gap-1.5">
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
        {label}
      </div>
      <div className={`mt-0.5 num ${bold ? "text-base md:text-lg font-bold" : "text-sm md:text-base font-semibold"} text-ink`}>
        {signed && !negative ? "+" : negative ? "−" : ""}{formatCr(Math.abs(value), { showZero: true })}
      </div>
    </div>
  );
}

function Legend({ color, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {dashed
        ? <span className="w-3 h-[2px]" style={{ background: `repeating-linear-gradient(90deg, ${color} 0 3px, transparent 3px 6px)` }} />
        : <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
      }
      {label}
    </span>
  );
}

/* ─────────────────────────── KPI hero card ─────────────────────────── */

function Hero({ icon: Icon, label, value, accent, testId, negative, bold }) {
  return (
    <div className="cf-card p-4 relative overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg" style={{ background: `${accent}22`, color: accent }}>
          <Icon size={13} />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold truncate">{label}</div>
      </div>
      <div className={`mt-2 num break-words ${bold ? "text-xl md:text-2xl lg:text-3xl font-bold" : "text-lg md:text-xl font-semibold"} text-ink`}>
        {negative && value > 0 ? "−" : ""}{formatCr(value)}
      </div>
      <div className="mt-3 h-[3px] w-12 rounded-full" style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}55 100%)` }} />
    </div>
  );
}
